/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import {
  Button,
  Dialog,
  IconButton,
  InlineNotification,
  LoadingSection,
  TextField,
  Typography2,
} from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

import GroupAssignmentStep from 'bundles/enterprise-admin-learners/components/invite-learners/GroupAssignmentStep';
import InviteOutcomeSummary from 'bundles/enterprise-admin-learners/components/invite-learners/InviteOutcomeSummary';
import {
  MAX_EMAIL_INVITE_ROWS,
  createEmptyEmailInviteRow,
  getEmailInviteRowErrors,
} from 'bundles/enterprise-admin-learners/components/invite-learners/emailInviteValidation';
import type {
  EmailInviteRow,
  EmailInviteRowErrors,
} from 'bundles/enterprise-admin-learners/components/invite-learners/emailInviteValidation';
import type { Invitee } from 'bundles/enterprise-admin-learners/components/invite-learners/types';
import type { InviteLearnersResult } from 'bundles/enterprise-admin-learners/hooks/useInviteLearners';
import { useInviteLearners } from 'bundles/enterprise-admin-learners/hooks/useInviteLearners';

import _t from 'i18n!nls/enterprise-admin-learners';

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  subtitle: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  rowList: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  row: css`
    display: flex;
    align-items: flex-end;
    gap: var(--cds-spacing-200);
  `,
  fields: css`
    display: flex;
    align-items: flex-end;
    flex: 1;
    gap: var(--cds-spacing-200);
  `,
  field: css`
    flex: 1;
  `,
  deleteButtonSpacer: css`
    width: var(--cds-spacing-600);
    flex-shrink: 0;
  `,
  fieldsHeader: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  addRowButton: css`
    margin-top: var(--cds-spacing-300);
    margin-bottom: var(--cds-spacing-100);
  `,
  primaryAction: css`
    margin-right: var(--cds-spacing-150);
  `,
  csvNotification: css`
    margin-bottom: var(--cds-spacing-300);
  `,
};

export type EmailInviteModalProps = {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  /** Set when the group is already known (Group Details lock, single-group admin) — skips GroupAssignmentStep. */
  autoSelectedGroupId?: string;
  /** Switches to the CSV method — offered once the row limit is reached. */
  onSwitchToCsv: () => void;
  /** Fired once `inviteLearners()` resolves successfully, so callers can refetch. */
  onInvited: () => void;
};

/**
 * Email/manual invite flow: up to 10 rows of { name, email }, validated on submit.
 * A pre-selected group (`autoSelectedGroupId`) skips GroupAssignmentStep and submits
 * immediately; otherwise the picker is shown after validation.
 */
const EmailInviteModal = ({
  open,
  onClose,
  onBack,
  autoSelectedGroupId,
  onSwitchToCsv,
  onInvited,
}: EmailInviteModalProps) => {
  const isAddToGroupFlow = Boolean(autoSelectedGroupId);
  const [rows, setRows] = React.useState<EmailInviteRow[]>(() => [createEmptyEmailInviteRow()]);
  const [rowErrors, setRowErrors] = React.useState<Record<string, EmailInviteRowErrors>>({});
  const [validatedInvitees, setValidatedInvitees] = React.useState<Invitee[] | null>(null);
  const [isCsvNotificationDismissed, setIsCsvNotificationDismissed] = React.useState(false);
  const isAtMaxRows = rows.length >= MAX_EMAIL_INVITE_ROWS;
  const { inviteLearners, submitting } = useInviteLearners();
  const [outcome, setOutcome] = React.useState<InviteLearnersResult | 'error' | null>(null);
  const hasAutoSubmitted = React.useRef(false);

  const handleInvite = React.useCallback<(groupId: string) => Promise<void>>(
    async (groupId) => {
      try {
        const result = await inviteLearners({ programId: groupId, invitees: validatedInvitees ?? [] });
        setOutcome(result);
        onInvited();
      } catch {
        setOutcome('error');
      }
    },
    [inviteLearners, validatedInvitees, onInvited]
  );

  React.useEffect(() => {
    if (!isAtMaxRows) setIsCsvNotificationDismissed(false);
  }, [isAtMaxRows]);

  React.useEffect(() => {
    // Reset on close so a later reopen never briefly shows a stale outcome/group-picker
    // screen before the parent's own step-reset effect swaps this component out.
    if (!open) {
      setRows([createEmptyEmailInviteRow()]);
      setRowErrors({});
      setValidatedInvitees(null);
      setOutcome(null);
      hasAutoSubmitted.current = false;
    }
  }, [open]);

  React.useEffect(() => {
    // Skip path: group already known, submit immediately once invitees are validated.
    if (validatedInvitees && autoSelectedGroupId && !hasAutoSubmitted.current) {
      hasAutoSubmitted.current = true;
      handleInvite(autoSelectedGroupId);
    }
  }, [validatedInvitees, autoSelectedGroupId, handleInvite]);

  const updateRow = (id: string, field: 'name' | 'email', value: string) => {
    setRows((prevRows) => prevRows.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    setRowErrors((prevErrors) => {
      const fieldErrorKey = field === 'name' ? 'nameError' : 'emailError';
      if (!prevErrors[id]?.[fieldErrorKey]) return prevErrors;
      const { [fieldErrorKey]: _removed, ...remainingRowErrors } = prevErrors[id];
      return { ...prevErrors, [id]: remainingRowErrors };
    });
  };

  const addRow = () => setRows((prevRows) => [...prevRows, createEmptyEmailInviteRow()]);

  const removeRow = (id: string) => {
    setRows((prevRows) => prevRows.filter((row) => row.id !== id));
    setRowErrors((prevErrors) => {
      const { [id]: _removed, ...remainingErrors } = prevErrors;
      return remainingErrors;
    });
  };

  const handleSubmit = () => {
    const nextRowErrors = getEmailInviteRowErrors(rows);
    setRowErrors(nextRowErrors);
    if (Object.keys(nextRowErrors).length > 0) return;

    setValidatedInvitees(rows.map(({ name, email }) => ({ name: name.trim(), email: email.trim() })));
  };

  if (outcome) {
    return <InviteOutcomeSummary open={open} outcome={outcome} onClose={onClose} />;
  }

  if (validatedInvitees && autoSelectedGroupId) {
    // Skip path: group already known, waiting on the auto-submit effect above to resolve.
    return (
      <Dialog open={open} onClose={() => {}} width="large">
        <HeadingGroup>{_t('Add learners')}</HeadingGroup>
        <Content>
          <LoadingSection label={_t('Adding your learners…')} />
        </Content>
        <Actions />
      </Dialog>
    );
  }

  if (validatedInvitees) {
    return (
      <GroupAssignmentStep
        open={open}
        onBack={() => setValidatedInvitees(null)}
        onSubmit={handleInvite}
        submitting={submitting}
      />
    );
  }

  return (
    <Dialog open={open} onClose={onClose} width="large">
      <HeadingGroup>{isAddToGroupFlow ? _t('Add learners via email') : _t('Invite learners via email')}</HeadingGroup>
      <Content>
        <Typography2 component="p" color="supportText" css={styles.subtitle}>
          {isAddToGroupFlow
            ? _t("Enter the names and email addresses of the learners you'd like to add.")
            : _t("Enter the names and email addresses of the learners you'd like to invite.")}
        </Typography2>
        <div role="status">
          {isAtMaxRows && !isCsvNotificationDismissed && (
            <InlineNotification
              severity="information"
              onDismiss={() => setIsCsvNotificationDismissed(true)}
              inlineAction={
                <Button variant="ghost" size="small" onClick={onSwitchToCsv}>
                  {_t('Upload CSV')}
                </Button>
              }
              css={styles.csvNotification}
            >
              {_t('Adding more than 10 learners? A CSV upload will be faster.')}
            </InlineNotification>
          )}
        </div>
        <div css={[styles.row, styles.fieldsHeader]}>
          <div css={styles.fields}>
            <Typography2 component="span" variant="subtitleMedium" css={styles.field}>
              {_t('Email')}
            </Typography2>
            <Typography2 component="span" variant="subtitleMedium" css={styles.field}>
              {_t('Name')}
            </Typography2>
          </div>
          <span css={styles.deleteButtonSpacer} />
        </div>
        <div css={styles.rowList}>
          {rows.map((row, index) => (
            <div css={styles.row} key={row.id}>
              <div css={styles.fields}>
                <TextField
                  aria-label={_t('Email')}
                  placeholder={_t('John.doe@example.com')}
                  value={row.email}
                  validationStatus={rowErrors[row.id]?.emailError ? 'error' : undefined}
                  validationLabel={rowErrors[row.id]?.emailError || undefined}
                  onChange={(event) => updateRow(row.id, 'email', event.target.value)}
                  inputProps={{ 'aria-required': true }}
                  css={styles.field}
                  fullWidth
                />
                <TextField
                  aria-label={_t('Name')}
                  placeholder={_t('John Doe')}
                  value={row.name}
                  validationStatus={rowErrors[row.id]?.nameError ? 'error' : undefined}
                  validationLabel={rowErrors[row.id]?.nameError || undefined}
                  onChange={(event) => updateRow(row.id, 'name', event.target.value)}
                  inputProps={{ 'aria-required': true }}
                  css={styles.field}
                  fullWidth
                />
              </div>
              <IconButton
                variant="ghost"
                intent="delete"
                disabled={rows.length <= 1}
                aria-label={_t('Remove learner #{rowNumber}', { rowNumber: index + 1 })}
                onClick={() => removeRow(row.id)}
                hideTooltip
              />
            </div>
          ))}
        </div>
        <Button
          variant="secondary"
          type="button"
          size="small"
          icon={<AddIcon />}
          iconPosition="before"
          onClick={addRow}
          css={styles.addRowButton}
        >
          {_t('Add learners')}
        </Button>
      </Content>
      <Actions>
        <Button variant="primary" onClick={handleSubmit} css={styles.primaryAction}>
          {isAddToGroupFlow ? _t('Add to group') : _t('Invite')}
        </Button>
        <Button variant="secondary" onClick={onBack}>
          {_t('Back')}
        </Button>
      </Actions>
    </Dialog>
  );
};

export default EmailInviteModal;
